import { useState, useCallback, useMemo, useEffect } from "react";
import { AVAILABLE_MODELS, DEFAULT_CHAT_CONFIG, DEFAULT_MODEL_ID, type ChatConfig, type ModelEntry, type EffortLevel } from "../shared/chat-config";
import { useAuth } from "./useAuth";

export function useChatConfig() {
  const { user } = useAuth();
  const [config, setConfig] = useState<ChatConfig>(DEFAULT_CHAT_CONFIG);
  // Live model registry from the gateway (set via setLiveModels after init frame).
  // null = pre-connect; falls back to AVAILABLE_MODELS until the first init.
  const [liveModels, setLiveModels] = useState<ModelEntry[] | null>(null);

  const userTier = user?.tier ?? "trial";
  const isVibecoder = userTier === "vibecoder";

  // The live model list from the gateway (via init frame). Falls back to the
  // pre-connect seed in chat-config.ts AVAILABLE_MODELS until the first init.
  // Intentional: trial users see ALL creator-tier models (generous trial = full
  // model access). Only VibeCoder-gated models are hidden for non-VibeCoder users.
  // Server enforces this via D1 tier check.
  //
  // Deliberately NOT filtering rollingAlias rows out of availableModels here —
  // this same list is used everywhere a model id needs to RESOLVE (current-model
  // display, pending-switch confirm-dialog labels, per-message model stamps),
  // not just where it's offered as a choice. Filtering it here would silently
  // break resolution for a session/sticky pick already on an alias. The picker
  // filter lives at the two actual render sites instead (InputControls.tsx,
  // ModelsPanel.tsx — see their groupBySubstrate() call).
  const baseModels = liveModels ?? AVAILABLE_MODELS;
  const availableModels = useMemo(
    () => baseModels.filter(m => m.minTier === "creator" || isVibecoder),
    [baseModels, isVibecoder]
  );

  // Keep currentModel and config.model in agreement — if the model ID
  // is somehow invalid (e.g. removed from allowlist), reset config to default.
  const found = baseModels.find(m => m.id === config.model);
  const currentModel = found ?? baseModels[0];

  // Gate this reset on liveModels !== null: while pre-connect (liveModels is null)
  // we only have the seed list, which may not contain a model stored from a previous
  // session (e.g. "claude-opus-4-8"). Don't reset a valid stored model just because
  // the seed doesn't list it — wait for the authoritative list from the init frame.
  //
  // Post-connect, reset to baseModels[0] (live registry first entry) rather than
  // DEFAULT_MODEL_ID so the fallback target is always a model the gateway serves.
  useEffect(() => {
    if (liveModels === null) return; // pre-connect: don't judge stored model against the seed
    if (!baseModels.find(m => m.id === config.model)) {
      setConfig(prev => ({ ...prev, model: baseModels[0]?.id ?? DEFAULT_MODEL_ID }));
    }
  }, [config.model, baseModels, liveModels]);

  // RC3 migration note: user.default_model is no longer used to seed config.model.
  // Per-companion model is now the single source of truth — it is persisted in
  // identity_preferences on the gateway and restored in the init frame (RC4 fix).
  // App.tsx passes onSyncChatModel into useMuseChat; the init handler calls it
  // synchronously (via onSyncChatModelRef) in the same React batch as setServerModel,
  // so chatConfig.model reflects the server model before the config_change self-seed
  // fires. This is the Option A fix that replaced the deleted useServerModelSync hook.
  //
  // user.default_thinking still applies here — it's a thinking preference, not a
  // model preference, and has no per-identity equivalent yet.
  //
  // Migration path for user.default_model: the /api/user/preferences endpoint and
  // the user object's default_model field are left intact (no breaking API change).
  // The Settings panel's Default Model control now edits config.model (via setModel)
  // rather than calling updateServerPref({default_model}). user.default_model will
  // eventually be deprecated once per-companion prefs fully replace it. The data is
  // safe — we just stop reading it as the picker initializer.
  //
  // Extract the primitive to avoid the eslint-disable on react-hooks/exhaustive-deps.
  // Depending on the primitive (boolean | undefined) is idempotent — same semantics
  // as optional-chaining on the object, but the linter can track it correctly.
  const defaultThinking = user?.default_thinking;
  useEffect(() => {
    if (defaultThinking) {
      setConfig(prev => ({ ...prev, thinking: true }));
    }
  }, [defaultThinking]);

  const setModel = useCallback((modelId: string, serverModels?: ModelEntry[]) => {
    // serverModels override: the init handler passes msg.models so the lookup
    // works even before liveModels populates (which happens one render later).
    const model = (serverModels ?? liveModels ?? AVAILABLE_MODELS).find(m => m.id === modelId);
    if (!model) return;
    setConfig(prev => ({
      ...prev,
      model: modelId,
      // Auto-enable thinking for native reasoning models,
      // preserve previous state for toggleable models,
      // disable for models without reasoning.
      thinking: model.nativeReasoning
        ? true
        : model.supportsReasoning
          ? prev.thinking
          : false,
    }));
  }, [liveModels]);

  const toggleThinking = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      thinking: currentModel.supportsReasoning ? !prev.thinking : false,
    }));
  }, [currentModel]);

  const setEffort = useCallback((effort: EffortLevel) => {
    setConfig(prev => ({ ...prev, effort }));
  }, []);

  // Kept intentionally — UI control removed in input bar redesign (2026-03-05)
  // but the config field + toggle survive for future re-wiring.
  const toggleAutoSummarize = useCallback(() => {
    setConfig(prev => ({ ...prev, autoSummarize: !prev.autoSummarize }));
  }, []);

  return { config, availableModels, currentModel, setModel, setEffort, toggleThinking, toggleAutoSummarize, setLiveModels };
}
